import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Add, AttachMoney } from '@mui/icons-material';
import { assignLabel, unassignLabel, useWatchlistLabels } from '../services/api';

/**
 * LabelDropdown - Checkbox list for assigning labels to a stock
 *
 * @param {Object} props
 * @param {string} props.symbol - Stock symbol
 * @param {string} props.userEmail - User's email
 * @param {function} props.onCreateLabel - Called to open label creation modal
 * @param {function} props.onPurchaseInfo - Called when user clicks label with trackPurchases enabled
 * @param {function} props.onClose - Called when selection is done
 */
const LabelDropdown = ({
  symbol,
  userEmail,
  onCreateLabel,
  onPurchaseInfo,
  onClose
}) => {
  const queryClient = useQueryClient();
  const { data: labelsData = { labels: [], assignments: {} } } = useWatchlistLabels(userEmail);
  const [pendingLabelId, setPendingLabelId] = useState(null);

  const stockAssignment = labelsData.assignments?.[symbol] || { labels: [], purchases: {} };
  const assignedLabelIds = stockAssignment.labels || [];

  const assignMutation = useMutation({
    mutationFn: assignLabel,
    onMutate: async ({ symbol, labelId }) => {
      setPendingLabelId(labelId);
      await queryClient.cancelQueries(['watchlistLabels', userEmail]);
      const previous = queryClient.getQueryData(['watchlistLabels', userEmail]);

      queryClient.setQueryData(['watchlistLabels', userEmail], (old) => {
        if (!old) return old;
        const newData = { ...old, assignments: { ...old.assignments } };
        if (!newData.assignments[symbol]) {
          newData.assignments[symbol] = { labels: [], purchases: {} };
        }
        newData.assignments[symbol] = {
          ...newData.assignments[symbol],
          labels: [...new Set([...newData.assignments[symbol].labels, labelId])]
        };
        return newData;
      });

      return { previous };
    },
    onError: (err, vars, context) => {
      queryClient.setQueryData(['watchlistLabels', userEmail], context.previous);
    },
    onSettled: () => {
      setPendingLabelId(null);
      queryClient.invalidateQueries(['watchlistLabels', userEmail]);
    }
  });

  const unassignMutation = useMutation({
    mutationFn: unassignLabel,
    onMutate: async ({ symbol, labelId }) => {
      setPendingLabelId(labelId);
      await queryClient.cancelQueries(['watchlistLabels', userEmail]);
      const previous = queryClient.getQueryData(['watchlistLabels', userEmail]);

      queryClient.setQueryData(['watchlistLabels', userEmail], (old) => {
        if (!old) return old;
        const newData = { ...old, assignments: { ...old.assignments } };
        if (newData.assignments[symbol]) {
          newData.assignments[symbol] = {
            ...newData.assignments[symbol],
            labels: newData.assignments[symbol].labels.filter((id) => id !== labelId)
          };
        }
        return newData;
      });

      return { previous };
    },
    onError: (err, vars, context) => {
      queryClient.setQueryData(['watchlistLabels', userEmail], context.previous);
    },
    onSettled: () => {
      setPendingLabelId(null);
      queryClient.invalidateQueries(['watchlistLabels', userEmail]);
    }
  });

  const handleLabelClick = (label) => {
    const isAssigned = assignedLabelIds.includes(label.id);

    if (isAssigned) {
      unassignMutation.mutate({ symbol, labelId: label.id });
    } else {
      // If label has purchase tracking and we're assigning, prompt for purchase info
      if (label.trackPurchases && onPurchaseInfo) {
        assignMutation.mutate({ symbol, labelId: label.id });
        // Trigger purchase info modal after assignment
        setTimeout(() => {
          onPurchaseInfo(symbol, label);
        }, 100);
      } else {
        assignMutation.mutate({ symbol, labelId: label.id });
      }
    }
  };

  const isPending = assignMutation.isLoading || unassignMutation.isLoading;

  return (
    <div className="min-w-[180px]">
      {labelsData.labels.length === 0 ? (
        <div className="px-3 py-2 text-sm text-gray-500">
          No labels yet
        </div>
      ) : (
        <div className="max-h-[200px] overflow-y-auto">
          {labelsData.labels.map((label) => {
            const isAssigned = assignedLabelIds.includes(label.id);
            const isLoading = pendingLabelId === label.id;
            const hasPurchase = stockAssignment.purchases?.[label.id];

            return (
              <button
                key={label.id}
                type="button"
                onClick={() => handleLabelClick(label)}
                disabled={isPending}
                className={`
                  w-full flex items-center gap-2 px-3 py-2 text-left text-sm
                  hover:bg-gray-50 transition-colors
                  ${isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                <span
                  className={`
                    w-4 h-4 rounded border flex items-center justify-center flex-shrink-0
                    ${isAssigned ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}
                  `}
                >
                  {isLoading ? (
                    <span className="w-2 h-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : isAssigned ? (
                    <Check style={{ fontSize: 12, color: 'white' }} />
                  ) : null}
                </span>

                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: label.color }}
                />

                <span className="flex-1 truncate">{label.name}</span>

                {label.trackPurchases && (
                  <AttachMoney
                    style={{ fontSize: 14 }}
                    className={hasPurchase ? 'text-green-500' : 'text-gray-300'}
                    title={hasPurchase ? 'Purchase tracked' : 'Track purchases'}
                  />
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="border-t border-gray-100">
        <button
          type="button"
          onClick={onCreateLabel}
          className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 transition-colors"
        >
          <Add style={{ fontSize: 16 }} />
          <span>Create Label</span>
        </button>
      </div>
    </div>
  );
};

export default LabelDropdown;
